package no.vareflow.web

import no.vareflow.app.domain.*

fun WebApp.renderCounting(root: El, user: UserAccount) {
    val session = shownOpenSession()
    if (session == null) {
        root.emptyCard(t("Ingen åpen måned. Opprett en ny måned i Oversikt.", "No open month. Create a new month in Overview."))
        return
    }
    val departments = core.departmentsForSession(session.id)
    val departmentId = core.selectedDepartmentId(session.id)
    // "Hva telles her?": only the hovedvarer of the chosen department.
    val categories = core.categoriesForDepartment(session.id, departmentId, core.visibleCategories(session.id, user))
    if (countCategory !in categories) countCategory = categories.firstOrNull()
    val selectedCategory = countCategory
    val sessionEditable = InventoryRules.countsAreEditable(session)
    val canSeePrice = AccessRules.hasPermission(user, Permission.PRODUCT_VIEW_PRICE)

    root.div("page-header") {
        div("page-titles") {
            h1(t("Telling", "Count"))
            p(formatYearMonth(session.yearMonth), "muted small")
        }
        if (modernUi) inventoryStatusBadge(session.status, true)
        else statusBadge(session.status.label, if (session.status == InventoryStatus.ACTIVE) Tone.GREEN else Tone.GREY)
    }
    // Counting only works in an Aktiv month: say so instead of showing locked fields without a reason.
    if (!sessionEditable) {
        root.div("card lock-note") {
            attr("role", "status")
            div("strong", t("${formatYearMonth(session.yearMonth)} er ${session.status.label.lowercase()}", "${formatYearMonth(session.yearMonth)} is ${session.status.label.lowercase()}"))
            p(if (session.status == InventoryStatus.DRAFT) t("Tellingen starter når måneden er aktivert. Til da kan antallene ikke endres.", "Counting starts when the month is activated. Until then the quantities cannot be changed.")
              else t("Måneden er låst. Antallene kan ikke endres.", "The month is locked. The quantities cannot be changed."), "small")
            if (session.status == InventoryStatus.DRAFT && (user.role == Role.ADMIN || AccessRules.hasPermission(user, Permission.INVENTORY_CLOSE))) {
                button(t("Aktiver nå", "Activate now"), "btn primary") {
                    if (core.activateInventorySession(session.id, user)) toast(t("${formatYearMonth(session.yearMonth)} er aktivert.", "${formatYearMonth(session.yearMonth)} is activated."))
                    else toast(t("Kan ikke aktivere: denne lokasjonen har allerede en aktiv telling.", "Cannot activate: this location already has an active count."))
                }
            }
        }
    }

    if (categories.isEmpty()) {
        root.emptyCard(
            if (departments.firstOrNull { it.id == departmentId }?.categories?.isNotEmpty() == true)
                t("Ingen av dine hovedvarer telles i denne avdelingen.", "None of your main categories are counted in this department.")
            else t("Du har ingen tildelte kategorier i denne tellingen.", "You have no assigned categories in this count."))
    } else {
        root.h2(t("Hovedvare", "Main category"), "section-title")
        root.div("segment-strip") {
            attr("data-scroll-key", "hovedvare")
            attr("role", "tablist")
            categories.forEach { category ->
                val selected = category == selectedCategory
                val counted = core.countedInDepartment(session.id, departmentId, category)
                val total = core.activeProductCount(category)
                val done = total > 0 && counted >= total
                el("button", "segment" + if (selected) " selected" else "") {
                    attr("type", "button")
                    attr("role", "tab")
                    attr("aria-selected", selected.toString())
                    div("segment-name") { span("clamp2", category) }
                    div("segment-count" + if (done && !selected) " done" else "",
                        if (done) "✓ $counted/$total" else "$counted/$total")
                    onClick {
                        flushPendingCounts()
                        countCategory = category
                        countSearch = ""
                        requestRender()
                    }
                }
            }
        }
    }

    root.h2(t("Avdeling", "Department"), "section-title")
    root.div("department-row") {
        div("chip-row") {
            attr("data-scroll-key", "avdeling")
            departments.forEach { department ->
                chip(department.name, department.id == departmentId) {
                    flushPendingCounts()
                    core.selectDepartment(session.id, department.id)
                }
            }
        }
        if (user.role == Role.ADMIN) {
            button("✎", "icon-btn outline") { manageDepartment(session.id, departments.firstOrNull { it.id == departmentId }, user) }
                .attr("aria-label", t("Rediger valgt avdeling", "Edit selected department")).attr("title", t("Rediger valgt avdeling", "Edit selected department"))
        }
        button("+", "icon-btn primary") { addDepartment(session.id, user) }
            .attr("aria-label", t("Ny avdeling", "New department")).attr("title", t("Ny avdeling", "New department"))
    }

    if (selectedCategory != null && countSearch.isBlank() && user.role == Role.ADMIN) {
        root.div("status-row") {
            button(t("Rediger hovedvare", "Edit main category"), "btn text small") { manageCategory(selectedCategory, user) }
        }
    }

    root.div("search") {
        svg("""<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="6.5"/><path d="m20 20-4.2-4.2"/></svg>""", "search-icon")
        input(countSearch, "count-search", "search-input", t("Søk etter produkt eller varenummer", "Search for product or item number"), "search") {
            countSearch = it
            requestRender()
        }.attr("aria-label", t("Søk etter produkt eller varenummer", "Search for product or item number"))
    }

    val allowed = core.products.filter { it.active && it.category in categories }
    val query = countSearch.trim().lowercase()
    val visible = if (query.isBlank()) {
        selectedCategory?.let { core.orderedProducts(it).filter { p -> p.active } }.orEmpty()
    } else {
        allowed.filter { p ->
            p.name.lowercase().contains(query) || p.varenummer.lowercase().contains(query) ||
                p.supplier.lowercase().contains(query) || p.category.lowercase().contains(query) ||
                p.aliases.any { it.lowercase().contains(query) }
        }.sortedWith { a, b -> compareNorwegian(a.name, b.name) }
    }

    if (visible.isNotEmpty()) {
        root.div("list-meta") {
            span("muted tiny", t("${visible.size} produkter", "${visible.size} products"))
            span("tiny strong", if (query.isBlank()) t("Sortert: Liste", "Sorted: List order") else t("Sortert: Navn", "Sorted: Name"))
        }
    }
    if (visible.isEmpty()) {
        root.emptyCard(if (countSearch.isBlank()) t("Ingen undervarer i denne hovedvaren ennå.", "No sub-items in this main category yet.") else t("Ingen produkter matcher søket.", "No products match the search."))
        return
    }
    root.div("count-list") {
        // Inside a hovedvare: sections per undergruppe (e.g. Vin → Rødvin, Hvitvin). A search is one flat list.
        val sections = if (query.isBlank()) groupByUndergruppe(visible) else listOf("" to visible)
        sections.forEach { (undergruppe, products) ->
            if (undergruppe.isNotEmpty()) div("subgroup-title", undergruppe).attr("role", "heading").attr("aria-level", "3")
            products.forEach { product ->
                val editable = sessionEditable && core.canEditCategory(session.id, product.category, user)
                countRow(this@renderCounting, session.id, departmentId, product, editable, countSearch.isNotBlank(), canSeePrice, user)
            }
        }
    }
}

private fun El.countRow(app: WebApp, sessionId: String, departmentId: String, product: Product, editable: Boolean, showCategory: Boolean, showPrice: Boolean, user: UserAccount) {
    val entry = app.core.entry(sessionId, product.id, departmentId)
    val key = app.pendingKey(sessionId, product.id, departmentId)
    val pending = app.pendingCounts[key]
    val text = pending?.text ?: entry?.quantity?.let(::formatQuantity).orEmpty()
    val shown = if (pending != null) WebApp.parseQuantity(pending.text) else entry?.quantity
    val note = entry?.note.orEmpty()

    div("count-row" + if (editable) "" else " locked") {
        span("glyph-dot", product.category.take(1).uppercase()).attr("aria-hidden", "true")
        div("count-info") {
            el("button", "count-name", product.name) {
                attr("type", "button")
                onClick { app.showProductDetails(product, showPrice, onSvinn = { app.svinnDialog(product, user) }) }
            }
            div("count-sub", buildString {
                if (product.varenummer.isNotBlank()) append("${product.varenummer} · ")
                if (showCategory) append(product.category + (if (product.undergruppe.isNotBlank()) " · ${product.undergruppe}" else "") + " · ")
                append(product.unit)
                if (showPrice) append(" · ${formatPrice(product.pricePerUnit)} kr/${product.unit}")
            })
            div("count-links") {
                button(if (note.isBlank()) t("Kommentar", "Comment") else t("Kommentar ✓", "Comment ✓"), "link small", enabled = editable || note.isNotBlank()) {
                    app.editComment(sessionId, departmentId, product, note, editable, user)
                }
                val svinnCount = app.core.svinn.count { it.productId == product.id && it.sessionId == sessionId }
                button(if (svinnCount == 0) t("Svinn", "Svinn") else t("Svinn ($svinnCount)", "Svinn ($svinnCount)"), "link small") {
                    app.svinnDialog(product, user)
                }.attr("data-key", "svinn-btn-${product.id}").attr("aria-label", t("Registrer svinn: ${product.name}", "Register svinn: ${product.name}"))
            }
        }
        div("stepper") {
            button("−", "step minus", enabled = editable && shown != null && shown > 0.0) {
                val next = ((shown ?: 0.0) - 1.0).coerceAtLeast(0.0)
                app.editCount(sessionId, product.id, departmentId, formatQuantity(next))
                app.requestRender()
            }.attr("aria-label", t("Trekk fra én: ${product.name}", "Subtract one: ${product.name}"))
            input(text, "qty-$key", "qty", "0").also { field ->
                field.on("input") {
                    val raw = field.node.value as String
                    val filtered = raw.filter { it.isDigit() || it == ',' || it == '.' }
                    val separators = filtered.count { it == ',' || it == '.' }
                    val accepted = if (separators <= 1) filtered else (app.pendingCounts[key]?.text ?: text)
                    if (accepted != raw) field.node.value = accepted
                    app.editCount(sessionId, product.id, departmentId, accepted)
                }
                field.attr("inputmode", "decimal")
                field.attr("aria-label", t("Antall: ${product.name}", "Quantity: ${product.name}"))
                field.disabled(!editable)
                field.on("blur") { app.commitCount(key) }
                field.on("keydown") { e -> if (e.key == "Enter") { app.commitCount(key); field.node.blur() } }
            }
            button("+", "step plus", enabled = editable) {
                app.editCount(sessionId, product.id, departmentId, formatQuantity((shown ?: 0.0) + 1.0))
                app.requestRender()
            }.attr("aria-label", t("Legg til én: ${product.name}", "Add one: ${product.name}"))
        }
    }
}

fun WebApp.showProductDetails(product: Product, canViewPrice: Boolean, onEdit: (() -> Unit)? = null, onSvinn: (() -> Unit)? = null) {
    openDialog(product.name) { close ->
        div("details") {
            detailRow(t("Varenummer", "Item number"), product.varenummer.ifBlank { t("Ikke registrert", "Not registered") })
            detailRow(t("Kategori", "Category"), product.category)
            if (product.undergruppe.isNotBlank()) detailRow(t("Undergruppe", "Subgroup"), product.undergruppe)
            detailRow(t("Enhet", "Unit"), product.unit)
            if (canViewPrice) detailRow(t("Innkjøpspris eks. MVA", "Purchase price excl. VAT"), "${formatPrice(product.pricePerUnit)} kr")
            detailRow(t("MVA", "VAT"), "${formatQuantity(product.vat)} %")
            detailRow(t("Leverandør", "Supplier"), product.supplier.ifBlank { t("Ikke registrert", "Not registered") })
            if (product.aliases.isNotEmpty()) detailRow(t("Aliaser", "Aliases"), product.aliases.joinToString())
            detailRow(t("Importstatus", "Import status"), importStatusLabel(product.importStatus))
            detailRow(t("Aktiv", "Active"), if (product.active) t("Ja", "Yes") else t("Nei", "No"))
            if (product.dataQuality.isNotBlank()) detailRow(t("Datakvalitet", "Data quality"), product.dataQuality)
        }
        dialogActions {
            if (onEdit != null) button(t("Rediger", "Edit"), "btn outline") { onEdit() }
            if (onSvinn != null) button(t("Registrer svinn", "Register svinn"), "btn outline") { onSvinn() }
            span("spacer")
            button(t("Lukk", "Close"), "btn primary") { close() }
        }
    }
}

private fun WebApp.editComment(sessionId: String, departmentId: String, product: Product, note: String, editable: Boolean, user: UserAccount) {
    val state = object { var text = note }
    openDialog(t("Kommentar · ${product.name}", "Comment · ${product.name}")) { close ->
        el("label", "field") {
            span("field-label", t("Kommentar", "Comment"))
            el("textarea", "input textarea") {
                attr("data-key", "dlg-comment")
                attr("rows", "3")
                node.value = state.text
                node.disabled = !editable
                on("input") { state.text = node.value as String }
            }
        }
        dialogActions {
            if (editable) button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(if (editable) t("Lagre", "Save") else t("Lukk", "Close"), "btn primary") {
                if (editable) {
                    flushPendingCounts()
                    val entry = core.entry(sessionId, product.id, departmentId)
                    core.saveCountEntry(sessionId, product.id, departmentId, entry?.quantity, state.text.trim(), user.id)
                }
                close()
            }
        }
    }
}

private fun WebApp.addDepartment(sessionId: String, user: UserAccount) = departmentDialog(sessionId, null, user)

private fun WebApp.manageDepartment(sessionId: String, department: InventoryDepartment?, user: UserAccount) {
    if (department != null) departmentDialog(sessionId, department, user)
}

/**
 * Ny / Rediger avdeling: the name and – for admins – "Hva telles her?": all hovedvarer, or only the
 * chosen ones (e.g. Wine cellar → Vin). Next month starts with the same departments.
 */
private fun WebApp.departmentDialog(sessionId: String, existing: InventoryDepartment?, user: UserAccount) {
    val isAdmin = user.role == Role.ADMIN
    val all = core.orderedCategories()
    val state = object {
        var name = existing?.name.orEmpty()
        var choose = existing?.categories?.isNotEmpty() == true
        val chosen = linkedSetOf<String>().apply { addAll(existing?.categories.orEmpty()) }
        var error: String? = null
        var confirmDelete = false
    }
    openDialog(if (existing == null) t("Ny avdeling", "New department") else t("Rediger avdeling", "Edit department")) { close ->
        if (state.confirmDelete && existing != null) {
            h2(t("Slett avdeling?", "Delete department?"), "dialog-subtitle")
            p(t("${existing.name} og de aktive telleverdiene i denne avdelingen slettes. Historiske snapshots beholdes.", "${existing.name} and the active count values in this department are deleted. Historical snapshots are kept."))
            dialogActions {
                button(t("Avbryt", "Cancel"), "btn text") { state.confirmDelete = false; requestRender() }
                button(t("Slett", "Delete"), "btn danger") {
                    close()
                    if (core.deleteDepartment(sessionId, existing.id, user)) toast(t("${existing.name} er slettet.", "${existing.name} has been deleted."))
                    else toast(t("Minst én avdeling må være igjen.", "At least one department must remain."))
                }
            }
            return@openDialog
        }
        field(t("Navn", "Name"), state.name, "dlg-name", placeholder = t("f.eks. Vinkjeller", "e.g. Wine cellar")) { state.name = it }
        if (isAdmin) {
            div("field") {
                span("field-label", t("Hva telles her?", "What is counted here?"))
                div("chip-row wrap") {
                    chip(t("Alle hovedvarer", "All main categories"), !state.choose) { state.choose = false; state.error = null; requestRender() }
                    chip(t("Velg hovedvarer", "Choose main categories"), state.choose) { state.choose = true; requestRender() }
                }
            }
            if (state.choose) {
                div("merge-list") {
                    all.forEachIndexed { i, c ->
                        checkbox(c, c in state.chosen, "dep-cat-$i") { on -> if (on) state.chosen += c else state.chosen -= c; state.error = null; requestRender() }
                    }
                }
                p(t("Nye hovedvarer kommer ikke med automatisk her – kryss dem av når de finnes.", "New main categories are not added here automatically – tick them when they exist."), "muted tiny")
            } else p(t("Alle hovedvarer telles i denne avdelingen, også nye.", "All main categories are counted in this department, also new ones."), "muted tiny")
        }
        errorText(state.error)
        dialogActions {
            if (existing != null && isAdmin) button(t("Slett", "Delete"), "btn text danger-text") { state.confirmDelete = true; requestRender() }
            span("spacer")
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(t("Lagre", "Save"), "btn primary") {
                val name = state.name.trim()
                val chosen = if (isAdmin && state.choose) all.filter { it in state.chosen } else emptyList()
                if (isAdmin && state.choose && chosen.isEmpty()) {
                    state.error = t("Velg minst én hovedvare – eller «Alle hovedvarer».", "Choose at least one main category – or «All main categories».")
                    requestRender(); return@button
                }
                val department = if (existing == null) core.addDepartment(sessionId, name, user)
                    else existing.takeIf { core.renameDepartment(sessionId, it.id, name, user) }
                if (department == null) {
                    state.error = t("Navnet er tomt eller brukes allerede.", "The name is empty or already in use.")
                    requestRender(); return@button
                }
                if (isAdmin) core.setDepartmentCategories(sessionId, department.id, chosen, user)
                close()
                toast(if (existing == null) t("$name er opprettet.", "$name has been created.") else t("$name er lagret.", "$name has been saved."))
            }
        }
    }
}

fun WebApp.manageCategory(category: String, user: UserAccount, onRenamed: (String) -> Unit = { countCategory = it }) {
    val count = core.products.count { it.category == category }
    nameDialog(
        t("Rediger hovedvare", "Edit main category"), category,
        deleteTitle = t("Slett hovedvare?", "Delete main category?"),
        deleteMessage = if (count == 1)
            t("$category inneholder 1 undervare. Hovedvaren og undervaren slettes fra produktregisteret. Historiske snapshots beholdes.", "$category contains 1 sub-item. The main category and the sub-item are deleted from the product register. Historical snapshots are kept.")
        else t("$category inneholder $count undervarer. Hovedvaren og alle undervarene slettes fra produktregisteret. Historiske snapshots beholdes.", "$category contains $count sub-items. The main category and all sub-items are deleted from the product register. Historical snapshots are kept."),
        onDelete = { if (core.deleteCategory(category, user)) toast(t("$category er slettet.", "$category has been deleted.")) }
    ) { name ->
        val existing = core.categoryNameTakenBy(category, name)
        if (existing != null) {
            // Same name as another hovedvare: offer to put the products together instead.
            setTimeout(0) { confirmMergeInto(listOf(category), existing, user, onRenamed) }
            return@nameDialog true
        }
        val ok = core.renameCategory(category, name, user)
        if (ok) { onRenamed(name); toast(t("Hovedvaren heter nå $name.", "The main category is now called $name.")) }
        ok
    }
}

/** "«X» finnes allerede – slå sammen?" and then the merge itself. */
fun WebApp.confirmMergeInto(sources: List<String>, target: String, user: UserAccount, onMerged: (String) -> Unit = {}) {
    val from = sources.filter { it != target }
    if (from.isEmpty()) return
    val moving = core.products.count { it.category in from }
    val already = core.products.count { it.category == target }
    val targetExists = target in core.categories
    openDialog(t("Slå sammen hovedvarer?", "Merge main categories?")) { close ->
        if (targetExists && from.size == 1) p(t("«$target» finnes allerede.", "«$target» already exists."), "strong")
        p(
            t("${if (moving == 1) "1 undervare" else "$moving undervarer"} fra ${from.joinToString(", ") { "«$it»" }} flyttes til «$target»",
                "${if (moving == 1) "1 sub-item" else "$moving sub-items"} from ${from.joinToString(", ") { "«$it»" }} move to «$target»") +
                (if (targetExists) t(" (som har $already fra før).", " (which already has $already).") else ".")
        )
        p(t("Ingenting slettes. Undergrupper, tellinger og tildelt arbeid følger med. ${from.joinToString(", ")} forsvinner som egne hovedvarer.",
            "Nothing is deleted. Subgroups, counts and assigned work come along. ${from.joinToString(", ")} disappear as separate main categories."), "muted small")
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(t("Slå sammen", "Merge"), "btn primary") {
                close()
                val result = core.mergeCategories(from, target, user)
                if (result != null) {
                    onMerged(result)
                    toast(t("$moving undervarer er samlet i $result.", "$moving sub-items are now in $result."))
                }
            }
        }
    }
}

/** Produkter → "Slå sammen": pick two or more hovedvarer and the name they end up under. */
fun WebApp.showMergeCategories(user: UserAccount) {
    val categories = core.orderedCategories()
    val state = object {
        val chosen = linkedSetOf<String>()
        var target: String? = null
        var newName = ""
        var useNewName = false
        /** The admin chose the target in the list: keep it while ticking more. */
        var picked = false
    }
    fun counted(c: String) = core.products.count { it.category == c }
    openDialog(t("Slå sammen hovedvarer", "Merge main categories")) { close ->
        p(t("Velg hovedvarene som skal bli én. Undervarene flyttes – ingenting slettes.", "Choose the main categories that should become one. The sub-items are moved – nothing is deleted."), "muted small")
        div("merge-list") {
            categories.forEach { c ->
                val n = counted(c)
                checkbox("$c · " + if (n == 1) t("1 undervare", "1 sub-item") else t("$n undervarer", "$n sub-items"), c in state.chosen, "merge-${categories.indexOf(c)}") { on ->
                    if (on) state.chosen += c else state.chosen -= c
                    if (!state.picked || state.target !in state.chosen) { state.picked = false; state.target = state.chosen.maxByOrNull { counted(it) } }
                    requestRender()
                }
            }
        }
        if (state.chosen.size >= 2) {
            div("field") {
                span("field-label", t("Samles under", "Combine under"))
                val options = state.chosen.map { it to it } + ("__new__" to t("Nytt navn …", "New name …"))
                selectBox(this@showMergeCategories, "merge-target", options, if (state.useNewName) "__new__" else state.target.orEmpty(), t("Samles under", "Combine under")) { v ->
                    if (v == "__new__") state.useNewName = true else { state.useNewName = false; state.target = v; state.picked = true }
                }
            }
            if (state.useNewName) field(t("Nytt navn", "New name"), state.newName, "merge-new-name", placeholder = t("f.eks. Frukt og grønt", "e.g. Fruit and veg")) { state.newName = it; requestRender() }
        }
        val target = if (state.useNewName) state.newName.trim() else state.target.orEmpty()
        val ready = state.chosen.size >= 2 && target.isNotBlank()
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(if (state.chosen.size >= 2) t("Slå sammen ${state.chosen.size}", "Merge ${state.chosen.size}") else t("Slå sammen", "Merge"), "btn primary", enabled = ready) {
                if (!ready) return@button
                val sources = state.chosen.toList()
                // A typed name that matches an existing hovedvare joins that one.
                val finalTarget = core.categoryNameTakenBy(null, target) ?: target
                setTimeout(0) { confirmMergeInto(sources, finalTarget, user) }
                close()
            }
        }
    }
}
